'use client'

import { IconToggleCheckOncheck } from 'icons/components/IconToggleCheckOncheck'
import { IconToggleDefault } from 'icons/components/IconToggleDefault'
import { ClientLabel } from 'labels/client'
import { useEffect, useState } from 'react'
import type { ChangeEvent, ReactNode } from 'react'
import { cx } from 'utils/cx'

import styles from './Checkbox.module.scss'

export interface CheckboxProps {
	id: string
	className?: string
	label: ReactNode
	name?: string
	checked?: boolean
	disabled?: boolean
	hasExtendedContent?: boolean
	errorMessage?: string
	errorPlaceholders?: Record<string, string | number>
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void
	value?: string
	dataTestId?: string
}

export const Checkbox = (props: CheckboxProps): React.ReactNode => {
	const {
		checked = false,
		disabled = false,
		errorMessage,
		errorPlaceholders,
		id,
		label,
		name = id,
		className = '',
		onChange = () => {
			/* do nothing */
		},
		hasExtendedContent = false,
		dataTestId,
		...checkboxAttributes
	} = props

	const [isChecked, setIsChecked] = useState(checked)

	function handleClick() {
		setIsChecked(!isChecked)
	}

	useEffect(() => {
		setIsChecked(checked)
	}, [checked])

	return (
		<div
			className={cx(
				styles.checkbox,
				className,
				disabled && styles.disabled,
				errorMessage && styles.error,
				!hasExtendedContent && styles.center
			)}
		>
			<label className={styles.box}>
				<input
					checked={isChecked}
					className={styles.input}
					disabled={disabled}
					id={id}
					name={name}
					onChange={onChange}
					onClick={handleClick}
					type='checkbox'
					data-testid={dataTestId}
					{...checkboxAttributes}
				/>

				{isChecked ? (
					<IconToggleCheckOncheck className={styles.icon} />
				) : (
					<IconToggleDefault className={styles.icon} />
				)}
				<div className={styles.label}>{label}</div>
			</label>
			{errorMessage && (
				<span
					className={cx(styles.textError, errorMessage && styles.showError)}
				>
					<ClientLabel placeholderValues={errorPlaceholders}>
						{errorMessage}
					</ClientLabel>
				</span>
			)}
		</div>
	)
}
